import { useState, useEffect, type DependencyList } from 'react';

export function useElementQuerySelector<T extends Element = HTMLElement>(
  selector: string,
  deps: DependencyList = []
): T | null {
  const [element, setElement] = useState<T | null>(null);

  useEffect(() => {
    // reset before re-searching
    setElement(null);

    // try to find it right away
    const find = (): boolean => {
      const element = document.querySelector<T>(selector);

      if (element) {
        setElement(element);

        return true;
      }

      return false;
    };

    if (find()) {
      // already there
      return;
    }

    // otherwise observe for additions
    const observer = new MutationObserver(() => {
      if (find()) {
        observer.disconnect();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();

    // re-run whenever selector OR any user-provided dep changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, ...deps]);

  return element;
}
